import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import queryString from 'query-string';
import React, { createContext, ReactNode, useContext, useEffect, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { SelectOption } from '../../components/Select';
import db_config from '../../configs/db_config';
import { IStadium } from '../../interfaces/types';
import { db } from '../../libs/firebase';
import { SeatInfoFormData } from '../../pages/SeatInfo/SeatInfoForm';
import { getBrowserLocale, getTermsAndConditions } from '../../utils';
import { getCurrentJstDate, getJstDateKey, isSameJstDate, parseJstDateTime, syncServerClock } from '../../utils/jstDate';
import i18n from '../I18n/I18n';
import { ISeatInfo, SeatFormOptions, useSeat } from './useSeat';

export type { ISeatInfo, SeatFormOptions };

interface SystemContextType {
  seatInfo: ISeatInfo | null;
  getFormattedSeat?: (idOrSeat?: string | any) => string;
  stadiumInfo: IStadium | null;
  enabledNewVersion: boolean;
  termsAndConditionsData: string | null;
  isPreOrder: boolean;
  /** True if we're still before game/delivery time for the given date (e.g. for form order_date). Used to decide SEAT_ORDER_AT_GAME_TIME modal. */
  isPreOrderWindowForDate: (orderDate: string) => boolean;
  getGameDayPreorder: (orderDate: string) => boolean;
  menuType: MenuType;
  updateSeatInfo: (info: SeatInfoFormData, isPreOrder: boolean) => Promise<{ pre_order: boolean }>;
  seatFormOptions: SeatFormOptions;
  isLoadingStadium: boolean;
  isLoadingSeat: boolean;
  error: string | null;
  fetchSeatData: (seatID: string) => Promise<void>;
  fetchAreasBySeatType: (seatType: string) => void;
  fetchAislesBySeatTypeAndArea: (seatType: string, area: string) => void;
  fetchAdditionalBySeatTypeAreaAndAisle: (seatType: string, area: string, aisle: string) => void;
  fetchRowsBySeatTypeAreaAisleAndAdditional: (seatType: string, area: string, aisle: string, additional: string) => void;
  fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow: (seatType: string, area: string, aisle: string, additional: string, row: string) => void;
  confirmSeatInfo: (isConfirmed?: boolean) => Promise<void>;
}

export enum MenuType {
  REGULAR_ORDER = 'regular_order',
  REGULAR_PRE_ORDER = 'regular_pre_order',
  SPECIAL_ORDER = 'special_order',
  SPECIAL_PRE_ORDER = 'special_pre_order',
}

const SystemContext = createContext<SystemContextType | undefined>(undefined);

export const SystemProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { t } = useTranslation();

  const [stadiumInfo, setStadiumInfo] = useState<IStadium | null>(null);
  const [gameDayDeliveryStartTimes, setGameDayDeliveryStartTimes] = useState<Record<string, Date>>({});
  const [gameDayPreorderMap, setGameDayPreorderMap] = useState<Record<string, boolean>>({});

  const [termsAndConditionsData, setTermsAndConditionsData] = useState<string | null>(null);
  const [isLoadingStadium, setIsLoadingStadium] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const seatID = (queryParams.seatID as string) || sessionStorage.getItem('seatID') || '';
  const stadiumID = (queryParams.stadiumID as string) || sessionStorage.getItem('stadiumID') || '';

  const {
    seatInfo,
    seatFormOptions,
    isLoadingSeat,
    error: seatError,
    fetchSeatData,
    fetchAllSeatsData,
    fetchAreasBySeatType,
    fetchAislesBySeatTypeAndArea,
    fetchAdditionalBySeatTypeAreaAndAisle,
    fetchRowsBySeatTypeAreaAisleAndAdditional,
    fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow,
    updateSeatInfo,
    confirmSeatInfo,
    getFormattedSeat,
    setSeatFormOrderDateOptions,
  } = useSeat(stadiumInfo);

  const [preOrderTick, setPreOrderTick] = useState(0);
  const preOrderTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (preOrderTimerRef.current) {
      clearTimeout(preOrderTimerRef.current);
      preOrderTimerRef.current = null;
    }
    if (!seatInfo?.order_date) return;
    try {
      if (!isSameJstDate(seatInfo.order_date)) return;
      const dateKey = getJstDateKey(seatInfo.order_date);
      const deliveryStart = gameDayDeliveryStartTimes[dateKey];
      if (!deliveryStart) return;
      const ms = deliveryStart.getTime() - getCurrentJstDate().getTime();
      if (ms <= 0) return;
      preOrderTimerRef.current = setTimeout(() => setPreOrderTick((t) => t + 1), ms + 100);
    } catch {
      // ignore
    }
    return () => {
      if (preOrderTimerRef.current) {
        clearTimeout(preOrderTimerRef.current);
        preOrderTimerRef.current = null;
      }
    };
  }, [seatInfo?.order_date, gameDayDeliveryStartTimes]);

  const isPreOrder = useMemo(() => {
    if (!seatInfo?.order_date) return false;
    try {
      const orderDateKey = getJstDateKey(seatInfo.order_date);
      if (gameDayPreorderMap[orderDateKey] !== undefined && !gameDayPreorderMap[orderDateKey]) return false;
      if (seatInfo.pre_order !== undefined && !seatInfo.pre_order) return false;
      if (!isSameJstDate(seatInfo.order_date)) return true;
      const deliveryStart = gameDayDeliveryStartTimes[orderDateKey];
      if (!deliveryStart) return false;
      return getCurrentJstDate() < deliveryStart;
    } catch {
      return false;
    }
  }, [seatInfo, gameDayDeliveryStartTimes, preOrderTick, gameDayPreorderMap]);

  const isPreOrderWindowForDate = useCallback(
    (orderDate: string): boolean => {
      if (!orderDate) return false;
      try {
        if (!isSameJstDate(orderDate)) return true; // future date -> still "pre-order window" for blocking purpose
        const dateKey = getJstDateKey(orderDate);
        const deliveryStart = gameDayDeliveryStartTimes[dateKey];
        if (!deliveryStart) return false; // no delivery start time → treat as regular window
        return getCurrentJstDate() < deliveryStart;
      } catch {
        return false;
      }
    },
    [gameDayDeliveryStartTimes]
  );

  const getGameDayPreorder = useCallback(
    (orderDate: string): boolean => {
      if (!orderDate) return true;
      try {
        const dateKey = getJstDateKey(orderDate);
        return gameDayPreorderMap[dateKey] !== false;
      } catch {
        return true;
      }
    },
    [gameDayPreorderMap]
  );

  const fetchOrderDateOptions = async (_stadiumId: string): Promise<SelectOption[]> => {
    const today = getJstDateKey();

    const stadiumDocRef = db.collection(db_config.DB_TABLE_STADIUMS_GAMEDAYS).doc(_stadiumId).collection('gamedays');
    const stadiumGameDaysDoc = await stadiumDocRef.where('date', '>=', today).get();

    const gameDays: SelectOption[] = [];
    const deliveryStartTimeMap: Record<string, Date> = {};
    const preorderMap: Record<string, boolean> = {};

    stadiumGameDaysDoc.docs.forEach((doc) => {
      const data = doc.data();

      const weekdayName = format(parseJstDateTime(data.date), 'E', { locale: ja });
      const isTodayDate = isSameJstDate(data.date);
      const isDisabled = data.preorder === false && !isTodayDate;
      const deliveryTimeValue = `${String(data.delivery_hh).padStart(2, '0')}:${String(data.delivery_mm || '00').padStart(2, '0')}`;
      const startTimeValue = `${String(data.starttime_hh).padStart(2, '0')}:${String(data.starttime_mm ?? '00').padStart(2, '0')}`;

      const [hours, minutes] = deliveryTimeValue.split(':');
      const deliveryStartTime = parseJstDateTime(data.date, parseInt(hours, 10), parseInt(minutes, 10));

      deliveryStartTimeMap[data.date] = deliveryStartTime;
      preorderMap[data.date] = data.preorder !== false;

      gameDays.push({
        value: deliveryStartTime.toString(),
        label: (
          <div className="inline-flex gap-2">
            {isTodayDate && <span className="font-bold">{t('TODAY')}</span>}
            <span>{format(parseJstDateTime(data.date), 'yyyy/MM/dd')}</span>
            <span>({weekdayName})</span>
            <span>{startTimeValue}</span>
          </div>
        ),
        disabled: isDisabled,
      });
    });

    setGameDayDeliveryStartTimes(deliveryStartTimeMap);
    setGameDayPreorderMap(preorderMap);
    return gameDays;
  };

  const fetchStadiumData = async () => {
    setIsLoadingStadium(true);
    setError(null);

    if (!stadiumID) {
      setIsLoadingStadium(false);
      return;
    }

    try {
      const serverClockSynced = await syncServerClock();
      if (serverClockSynced) {
        setPreOrderTick((tick) => tick + 1);
      }

      const stadiumDocRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE).doc(stadiumID);
      const stadiumDoc = await stadiumDocRef.get();

      if (!stadiumDoc.exists) {
        setError('Stadium not found');
        setStadiumInfo(null);
        setIsLoadingStadium(false);
        return;
      }

      const stadium = stadiumDoc.data() as IStadium;

      const lang = stadium.language ?? getBrowserLocale();

      i18n.changeLanguage(lang);
      i18n.options.lng = lang;
      i18n.options.fallbackLng = lang;

      setStadiumInfo({ ...stadium, id: stadiumDoc.id, language: lang });

      sessionStorage.setItem('stadiumID', stadiumDoc.id);

      const enable_terms = stadium?.enable_terms ?? false;
      if (enable_terms) {
        const terms_and_conditions = await getTermsAndConditions(lang, stadiumDoc.id);
        setTermsAndConditionsData(terms_and_conditions ?? null);
      }

      const orderDateOptions = await fetchOrderDateOptions(stadiumDoc.id);
      setSeatFormOrderDateOptions(orderDateOptions);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch data';
      setError(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setIsLoadingStadium(false);
    }
  };

  useEffect(() => {
    fetchStadiumData();
  }, [stadiumID]);

  useEffect(() => {
    if (stadiumInfo?.id) {
      fetchAllSeatsData();
    }
  }, [stadiumInfo?.id]);

  useEffect(() => {
    if (seatID && stadiumInfo?.id) {
      fetchSeatData(seatID);
    }
  }, [seatID, stadiumInfo?.id]);

  const enabledNewVersion = useMemo(() => {
    if (!stadiumInfo?.id) return false;
    return stadiumInfo?.cms_settings?.pre_order ?? false;
  }, [stadiumInfo]);

  const menuType = useMemo(() => {
    const result = isPreOrder
      ? seatInfo?.special_order
        ? MenuType.SPECIAL_PRE_ORDER
        : MenuType.REGULAR_PRE_ORDER
      : seatInfo?.special_order
        ? MenuType.SPECIAL_ORDER
        : MenuType.REGULAR_ORDER;

    return result;
  }, [seatInfo, isPreOrder]);

  return (
    <SystemContext.Provider
      value={{
        seatInfo,
        stadiumInfo,
        termsAndConditionsData,
        enabledNewVersion,
        updateSeatInfo,
        confirmSeatInfo,
        seatFormOptions,
        isLoadingStadium,
        isLoadingSeat,
        error: error || seatError,
        getFormattedSeat,
        menuType,
        isPreOrder,
        isPreOrderWindowForDate,
        getGameDayPreorder,
        fetchSeatData,
        fetchAreasBySeatType,
        fetchAislesBySeatTypeAndArea,
        fetchAdditionalBySeatTypeAreaAndAisle,
        fetchRowsBySeatTypeAreaAisleAndAdditional,
        fetchSeatNumbersBySeatTypeAreaAisleAdditionalAndRow,
      }}
    >
      {children}
    </SystemContext.Provider>
  );
};

export const useSystemContext = () => {
  const context = useContext(SystemContext);
  if (context === undefined) {
    throw new Error('useSystemContext must be used within a SystemProvider');
  }
  return context;
};
